import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { LanguageService } from '../../../../../core/services/language.service';
import { LiffService } from '../../../../../core/services/liff.service';
import { ApiServiceCaseService } from '../../../../../core/services/api/Customer-service/api-customer-service';
import { PssLoadingComponent } from '../../../../../tools/template/pss/pss-loading/pss-loading.component';
import { ToastCustomService } from '../../../../../tools/services/toast/toast-custom/toastCustom.service';
import {
  CustomerServiceCaseDetail,
  CustomerServiceCaseRow,
} from '../../../../../core/models/customer-service/service-case.model';
import {
  mapCustomerMyServiceCasesDtoToModel,
  mapCustomerServiceCaseDetailDtoToModel,
} from '../../../../../core/mappers/service-case.mapper';

@Component({
  selector: 'app-customer-service-case',
  standalone: true,
  imports: [CommonModule, FormsModule, PssLoadingComponent],
  templateUrl: './customer-service-case.component.html',
  styleUrl: './customer-service-case.component.css',
})
export class CustomerServiceCaseComponent implements OnInit {
  currentLang: 'TH' | 'EN' = 'TH';
  uid: string = '';
  isLoading: boolean = false;

  // แท็บและการค้นหา
  activeTab: 'active' | 'history' = 'active';
  searchText: string = '';

  // รายการเคส
  activeCases: CustomerServiceCaseRow[] = [];
  historyCases: CustomerServiceCaseRow[] = [];

  // รายละเอียดเคสและ Modal
  selectedCaseDetail: CustomerServiceCaseDetail | null = null;
  showDetailModal: boolean = false;

  constructor(
    private languageService: LanguageService,
    private liffService: LiffService,
    private apiServiceCase: ApiServiceCaseService,
    private toastCustom: ToastCustomService,
    private router: Router,
    private route: ActivatedRoute,
  ) { }

  ngOnInit(): void {
    this.languageService.currentLang$.subscribe((lang) => {
      this.currentLang = lang;
    });

    this.uid = this.liffService.getUserId();
    this.loadMyServiceCases();

    // รองรับ Deep Link จาก Query Parameters (เช่น ?id=42 หรือ ?caseNo=CASE-...)
    this.route.queryParams.subscribe((params) => {
      const caseIdParam = params['id'] ? parseInt(params['id'], 10) : undefined;
      const caseNoParam = params['caseNo'] ? params['caseNo'] : undefined;
      if (caseIdParam || caseNoParam) {
        this.openCaseDetail(caseIdParam, caseNoParam);
      }
    });
  }

  /*#################################################### Function ################################################*/

  /**
   * ย้อนกลับไปยังหน้าก่อนหน้า
   */
  goback(): void {
    setTimeout(() => {
      this.router.navigate(['/report-main']);
    }, 0);
  }

  /**
   * สลับแท็บระหว่าง กำลังดำเนินการ (active) และ ประวัติ (history)
   */
  switchTab(tab: 'active' | 'history'): void {
    this.activeTab = tab;
  }

  /**
   * Getter สำหรับกรองรายการเคสตามคำค้นหา
   */
  get displayedCases(): CustomerServiceCaseRow[] {
    const list = this.activeTab === 'active' ? this.activeCases : this.historyCases;
    if (!this.searchText || this.searchText.trim() === '') {
      return list;
    }
    const query = this.searchText.toLowerCase().trim();
    return list.filter(
      (item) =>
        item.caseNo.toLowerCase().includes(query) ||
        item.subject.toLowerCase().includes(query) ||
        item.issueTypeName.toLowerCase().includes(query) ||
        item.siteName.toLowerCase().includes(query) ||
        item.statusLabel.toLowerCase().includes(query),
    );
  }

  /**
   * คืนค่า Badge Style CSS ตามสถานะของเคส
   */
  getStatusBadgeClass(status: string): string {
    switch (status) {
      case 'received':
        return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'in_progress':
        return 'bg-amber-50 text-amber-800 border-amber-200';
      case 'waiting_customer':
        return 'bg-purple-50 text-purple-700 border-purple-200';
      case 'awaiting_customer_confirm':
        return 'bg-orange-50 text-orange-800 border-orange-200';
      case 'closed':
        return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'cancelled':
        return 'bg-rose-50 text-rose-700 border-rose-200';
      default:
        return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  }

  /**
   * เปิด Modal แสดงรายละเอียดเคส
   */
  openCaseDetail(id?: number, caseNo?: string): void {
    this.loadCaseDetailApi(id, caseNo);
  }

  /**
   * ปิด Modal รายละเอียดเคส
   */
  closeDetailModal(): void {
    this.showDetailModal = false;
    this.selectedCaseDetail = null;
  }

  /*############################################# API #################################################*/

  /**
   * ดึงรายการแจ้งเรื่องทั้งหมดของผู้ใช้ (active / history)
   */
  loadMyServiceCases(): void {
    if (!this.uid) {
      return;
    }
    this.isLoading = true;
    this.apiServiceCase.getMyServiceCases(this.uid).subscribe({
      next: (res) => {
        this.isLoading = false;
        const mapped = mapCustomerMyServiceCasesDtoToModel(res);
        this.activeCases = mapped.active;
        this.historyCases = mapped.history;
      },
      error: (err) => {
        this.isLoading = false;
        console.error('Error loading my service cases:', err);
        this.toastCustom.showError(
          this.currentLang === 'TH' ? 'ไม่สามารถโหลดรายการเรื่องแจ้งได้' : 'Failed to load service cases',
        );
      },
    });
  }

  /**
   * ดึงรายละเอียดเรื่องตาม ID หรือ CaseNo
   */
  loadCaseDetailApi(id?: number, caseNo?: string): void {
    if (!this.uid) {
      return;
    }
    this.isLoading = true;
    this.apiServiceCase.getMyServiceCaseDetail(this.uid, id, caseNo).subscribe({
      next: (res) => {
        this.isLoading = false;
        this.selectedCaseDetail = mapCustomerServiceCaseDetailDtoToModel(res);
        this.showDetailModal = true;
      },
      error: (err) => {
        this.isLoading = false;
        console.error('Error loading case detail:', err);
        this.toastCustom.showError(
          this.currentLang === 'TH' ? 'ไม่พบรายละเอียดเรื่องที่ร้องเรียน' : 'Service case detail not found',
        );
      },
    });
  }
}
